import { useEffect, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Stack } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { Alert, backendServices, Card, Loading } from 'common';
import { useCompanyPublicData, useCompanySubmissionMethods } from '../../../services';
import { SubmissionStep } from './SubmissionStep';
import { ContactInformation, PageWithCustomLefNav } from '../../../components';

export type SubmissionGuideLocationState = {
    setupFailed?: boolean;
};

function safelyParseJSON(payload: any) {
    try {
        const parsedJSON = JSON.parse(payload);

        return [parsedJSON, null];
    } catch (e) {
        console.error('There was an issue converting data to JSON');

        return [null, e];
    }
}

function useRouteData() {
    const history = useHistory();

    const { companyToken = '', submissionMethod: submissionMethodKey = null } = useParams<{
        companyToken?: string;
        submissionMethod?: string;
    }>();

    const submissionMethod = useMemo<backendServices.ViewModels.SubmissionMethod | null>(() => {
        const method = backendServices.ViewModels.SubmissionMethod[submissionMethodKey as any] as string | number;

        if (typeof method !== 'number') {
            return null;
        }

        return method;
    }, [submissionMethodKey]);

    useEffect(() => {
        if (submissionMethod != null) {
            return;
        }

        /**
         * This will redirect the user to the Transcepta website.
         */
        history.replace('/');
    }, [history, submissionMethod]);

    return { companyToken, submissionMethod: submissionMethod };
}

type SubmissionMethodQuery =
    | {
          status: 'success';
          data: backendServices.ViewModels.SubmissionGuideLayoutViewModel;
      }
    | { status: 'error'; message: string }
    | { status: 'loading' };

function useSubmissionMethodLayout({
    companyToken,
    targetSubmissionMethod,
}: {
    companyToken: string;
    targetSubmissionMethod: backendServices.ViewModels.SubmissionMethod | null;
}): SubmissionMethodQuery {
    const history = useHistory();
    const {
        data: company,
        isLoading: isLoadingCompany,
        error: companyError,
    } = useCompanyPublicData({ search: companyToken });

    const {
        data: submissionMethods,
        isLoading: isLoadingMethods,
        error: methodsError,
    } = useCompanySubmissionMethods({ companyId: company?.ID });

    const submissionMethod = submissionMethods?.filter(
        (method) => targetSubmissionMethod !== null && method.SubmissionMethod === targetSubmissionMethod
    )[0];

    useEffect(() => {
        if (submissionMethod == null && submissionMethods != null) {
            /**
             * This will redirect the user to the Transcepta website.
             */
            history.replace('/');
        }
    }, [history, submissionMethod, submissionMethods]);

    const parsedData = useMemo<
        | {
              submissionGuide: backendServices.ViewModels.SubmissionGuideLayoutViewModel;
              error: null;
          }
        | { error: string }
        | undefined
    >(() => {
        if (submissionMethod == null) {
            return undefined;
        }

        const { SubmissionGuideLayout } = submissionMethod;

        if (typeof SubmissionGuideLayout !== 'string') {
            return undefined;
        }

        const [guideLayout, parseError] = safelyParseJSON(SubmissionGuideLayout);

        if (parseError) {
            return {
                error: `${parseError}`,
            };
        }

        const parsedLayout = backendServices.ViewModels.SubmissionGuideLayoutSchema.safeParse(guideLayout);

        if (parsedLayout.success) {
            return {
                submissionGuide: parsedLayout.data,
                error: null,
            };
        }

        const error = parsedLayout.error.errors.map((e) => e.message).join(', ');

        console.error(`There were issues parsing "SubmissionGuideLayout": ${error}`);

        return {
            error: error,
        };
    }, [submissionMethod]);

    if (isLoadingCompany || isLoadingMethods) {
        return {
            status: 'loading',
        };
    }

    if (companyError) {
        return {
            status: 'error',
            message: "We couldn't find this company.",
        };
    }

    if (methodsError) {
        return {
            status: 'error',
            message: "We couldn't find the submission instructions for this company.",
        };
    }

    if (parsedData?.error !== null) {
        return {
            status: 'error',
            message: "The data for this company isn't configured correctly.",
        };
    }

    return {
        status: 'success',
        data: parsedData.submissionGuide,
    };
}

export function SubmissionGuide() {
    const { companyToken, submissionMethod } = useRouteData();
    const location = useLocation<SubmissionGuideLocationState | undefined>();

    const { data: company, isLoading: isLoadingCompany } = useCompanyPublicData({ search: companyToken });

    const layoutQuery = useSubmissionMethodLayout({ companyToken, targetSubmissionMethod: submissionMethod });

    const contactInfo = (
        <ContactInformation
            companyName={company?.Name ?? ''}
            links={{
                RegistrationContact: company?.RegistrationContact ?? '',
                InvitationLink: company?.InvitationLink ?? null,
            }}
        />
    );

    const pageContent = (() => {
        if (layoutQuery.status === 'loading') {
            return (
                <Stack height="100px" pt={12}>
                    <Loading />
                </Stack>
            );
        }

        if (layoutQuery.status === 'error') {
            return (
                <Stack height="100px" pt={12}>
                    <Alert severity="error" testId="error-alert">
                        {layoutQuery.message}
                    </Alert>
                </Stack>
            );
        }

        return (
            <Stack
                spacing={4}
                sx={{
                    width: '100%',
                    maxWidth: (t) => t.breakpoints.values.lg,
                    pb: 16,
                }}
                divider={
                    <Stack alignItems="center">
                        <KeyboardArrowDownIcon
                            sx={{
                                color: (t) => t.palette.grey[800],
                            }}
                        />
                    </Stack>
                }
            >
                {layoutQuery.data.steps.map((step) => (
                    <SubmissionStep key={step.id} step={step} />
                ))}
            </Stack>
        );
    })();

    return (
        <PageWithCustomLefNav
            leftNavContent={
                isLoadingCompany ? (
                    <Stack height="100px" pt={{ lg: 12 }}>
                        <Loading />
                    </Stack>
                ) : (
                    <>
                        <Stack
                            width="100%"
                            sx={{
                                display: {
                                    xs: 'none',
                                    lg: 'flex',
                                },
                            }}
                        >
                            {contactInfo}
                        </Stack>
                        <Card
                            rootProps={{
                                sx: { display: { lg: 'none' } },
                            }}
                            content={contactInfo}
                        />
                    </>
                )
            }
        >
            {location.state?.setupFailed && (
                <Alert severity="error" testId="post-signup-setup-failed-alert">
                    Something went wrong loading the next steps. Please contact support.
                </Alert>
            )}
            {pageContent}
        </PageWithCustomLefNav>
    );
}
